import { FunctionComponent, ReactNode, RefObject, useRef, useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFaceSmile } from '@fortawesome/pro-regular-svg-icons';
import Picker, { IEmojiData, SKIN_TONE_LIGHT } from 'emoji-picker-react';
import ClickAwayListener from 'react-click-away-listener';
import styles from './SMSMessageEditor.module.scss';
import clsx from 'clsx';

interface Props {
    value: string;
    onChange: (value: string) => void;
    maxLength?: number;
    toolbar?: ReactNode;
    textareaRef?: RefObject<HTMLTextAreaElement>;
    hasMergeTags?: boolean;
}

function messageInfo(text: string, approximate = false): string {
    const len = text.length;
    const prefix = approximate ? '~' : '';
    if (len === 0) return `0/160 (1 message)`;
    const messages = Math.ceil(len / 160);
    const remainder = len % 160 === 0 ? 160 : len % 160;
    // A tag like [Client Name] is 13 characters here but becomes whatever the
    // recipient's name is at send time, so an exact count would be a lie.
    const note = approximate ? ', tags may expand' : '';
    return `${prefix}${remainder}/160 (${messages} message${messages > 1 ? 's' : ''}${note})`;
}

const SMSMessageEditor: FunctionComponent<Props> = ({
    value,
    onChange,
    maxLength = 1600,
    toolbar,
    textareaRef: externalRef,
    hasMergeTags = false
}) => {
    const internalRef = useRef<HTMLTextAreaElement>(null);
    const textareaRef = externalRef ?? internalRef;
    const [focused, setFocused] = useState(false);
    const [showEmoji, setShowEmoji] = useState(false);

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        if (e.target.value.length <= maxLength) {
            onChange(e.target.value);
        }
    };

    const onEmojiClick = (_e: React.MouseEvent, emojiObject: IEmojiData) => {
        setShowEmoji(false);
        const el = textareaRef.current;
        if (!el) return;
        const start = el.selectionStart ?? value.length;
        const end = el.selectionEnd ?? value.length;
        const next = value.slice(0, start) + emojiObject.emoji + value.slice(end);
        if (next.length <= maxLength) {
            onChange(next);
            requestAnimationFrame(() => {
                el.focus();
                el.setSelectionRange(start + emojiObject.emoji.length, start + emojiObject.emoji.length);
            });
        }
    };

    return (
        <div className={clsx(styles.root, { [styles.focused]: focused })}>
            <textarea
                ref={textareaRef}
                className={styles.textarea}
                value={value}
                placeholder="Type your message…"
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                onChange={handleChange}
            />
            <div className={styles.toolbar}>
                <div className={styles.toolbarLeft}>
                    <ClickAwayListener onClickAway={() => setShowEmoji(false)}>
                        <div className={styles.emojiWrapper}>
                            <button
                                type="button"
                                className={styles.emojiBtn}
                                onClick={() => setShowEmoji(v => !v)}
                            >
                                <FontAwesomeIcon icon={faFaceSmile} />
                            </button>
                            {showEmoji && (
                                <div className={styles.emojiPicker}>
                                    <Picker
                                        onEmojiClick={onEmojiClick}
                                        skinTone={SKIN_TONE_LIGHT}
                                        native
                                        disableAutoFocus
                                    />
                                </div>
                            )}
                        </div>
                    </ClickAwayListener>
                    {toolbar}
                </div>
                <span className={styles.counter}>{messageInfo(value, hasMergeTags)}</span>
            </div>
        </div>
    );
};

export default SMSMessageEditor;
